import { useRef, useEffect, useCallback, useState } from 'react';
import type { SpriteGrid, PaletteColors } from './avatarTypes';

interface PartOption {
  id: string;
  name: string;
  grid: SpriteGrid;
  category?: string;
}

interface PartPickerProps {
  options: PartOption[];
  selected: string;
  onSelect: (id: string) => void;
  onHover?: (id: string | null) => void;
  palette: PaletteColors;
  /** Which palette index to use as the main color for mini previews */
  previewColorIndex?: number;
}

/** Render a mini sprite preview into a small canvas */
function MiniPreview({
  grid,
  palette,
  colorIndex,
  size = 48,
}: {
  grid: SpriteGrid;
  palette: PaletteColors;
  colorIndex: number;
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || grid.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rows = grid.length;
    const cols = Math.max(...grid.map(r => r.length));
    const scale = Math.floor(size / Math.max(rows, cols));

    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = false;

    const offsetX = Math.floor((size - cols * scale) / 2);
    const offsetY = Math.floor((size - rows * scale) / 2);

    const colorMap: Record<number, string> = {
      1: palette.bodyPrimary,
      2: palette.bodySecondary,
      3: palette.eyeColor,
      4: palette.mouthColor,
      5: palette.accessoryColor,
    };

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        const idx = grid[r][c];
        if (idx === 0) continue;
        ctx.fillStyle = colorMap[idx] || colorMap[colorIndex] || palette.bodyPrimary;
        ctx.fillRect(offsetX + c * scale, offsetY + r * scale, scale, scale);
      }
    }
  }, [grid, palette, colorIndex, size]);

  if (grid.length === 0) {
    return (
      <div
        className="malpedia-avatar-mini-preview malpedia-avatar-mini-preview--empty"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="malpedia-avatar-mini-preview"
      width={size}
      height={size}
      style={{ width: size, height: size, imageRendering: 'pixelated' }}
    />
  );
}

export default function PartPicker({
  options,
  selected,
  onSelect,
  onHover,
  palette,
  previewColorIndex = 1,
}: PartPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const idx = options.findIndex(o => o.id === selected);
      let next = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = Math.min(idx + 1, options.length - 1);
        e.preventDefault();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = Math.max(idx - 1, 0);
        e.preventDefault();
      } else if (e.key === 'Enter') {
        onSelect(options[idx]?.id ?? selected);
        return;
      }
      if (next !== idx) {
        onSelect(options[next].id);
      }
    },
    [options, selected, onSelect],
  );

  const hoveredOption = hoveredId ? options.find(o => o.id === hoveredId) : null;

  return (
    <div
      className="malpedia-avatar-part-grid"
      ref={containerRef}
      role="listbox"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {options.map((opt) => {
        const isSelected = opt.id === selected;
        const isHovered = opt.id === hoveredId;
        let cls = 'malpedia-avatar-part-card';
        if (isSelected) cls += ' malpedia-avatar-part-card--selected';
        return (
          <div key={opt.id} className="malpedia-avatar-part-card-wrapper">
            <button
              className={cls}
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(opt.id)}
              onMouseEnter={() => { setHoveredId(opt.id); onHover?.(opt.id); }}
              onMouseLeave={() => { setHoveredId(null); onHover?.(null); }}
            >
              <MiniPreview
                grid={opt.grid}
                palette={palette}
                colorIndex={previewColorIndex}
              />
              <span className="malpedia-avatar-part-label">{opt.name}</span>
            </button>
            {/* Hover popover with enlarged preview */}
            {isHovered && hoveredOption && hoveredOption.grid.length > 0 && (
              <div className="malpedia-avatar-hover-popover">
                <MiniPreview
                  grid={hoveredOption.grid}
                  palette={palette}
                  colorIndex={previewColorIndex}
                  size={128}
                />
                <span className="malpedia-avatar-hover-popover-name">{hoveredOption.name}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
